import type { SortingState } from "@tanstack/react-table";
import { addDays } from "date-fns";
import { z } from "zod";
import type { FieldOptions } from "@/features/attorney/views/Entries/filters/FilterMenu";
import type { EntryFilterState } from "@/features/attorney/views/Entries/filters/types";
import type { Timekeeper } from "@/features/attorney/views/Entries/timekeepers";
import type { Matter } from "@/shared/cache";

const filterOptionSchema = z.object({
	value: z.union([z.string(), z.number(), z.boolean()]),
	label: z.string(),
	email: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	code: z.string().nullable().optional(),
	client: z.string().nullable().optional(),
	billableCategory: z
		.enum(["billable", "non_billable", "billable_credit", "unknown"])
		.nullable()
		.optional(),
	billingCategory: z
		.enum([
			"hourly",
			"flat_rate",
			"pro_bono",
			"administrative",
			"internal",
			"unknown",
		])
		.nullable()
		.optional(),
});

const filtersResponseSchema = z.object({
	object: z.literal("entry_group_filters"),
	statuses: z.array(filterOptionSchema),
	applications: z.array(filterOptionSchema),
	matters: z.array(filterOptionSchema),
	clients: z.array(filterOptionSchema),
	matterBillableCategories: z.array(filterOptionSchema),
	taskCodes: z.array(filterOptionSchema),
	activityCodes: z.array(filterOptionSchema),
	timekeepers: z.array(filterOptionSchema),
});

export const entryGroupStatsSchema = z.object({
	object: z.literal("entry_group_stats"),
	groupCount: z.number(),
	entryCount: z.number(),
	totalSeconds: z.number(),
	byStatus: z.object({
		tracked: z.number(),
		reviewed: z.number(),
		exported: z.number(),
		deleted: z.number(),
	}),
	byBillableCategory: z.object({
		billable: z.number(),
		billableCredit: z.number(),
		nonBillable: z.number(),
		unknown: z.number(),
	}),
});

export type AdminEntryFiltersResponse = z.infer<typeof filtersResponseSchema>;
export type EntryGroupStats = z.infer<typeof entryGroupStatsSchema>;

export type AdminEntryFilterData = {
	fieldOptions: FieldOptions;
	timekeepers: Timekeeper[];
	applications: Map<string, string>;
	matters: Matter[];
};

function value(option: z.infer<typeof filterOptionSchema>): string {
	return String(option.value);
}

function splitName(fullName: string): {
	firstName: string;
	lastName: string;
} {
	const parts = fullName.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return { firstName: "", lastName: "" };
	if (parts.length === 1) {
		return { firstName: parts[0], lastName: parts[0] };
	}
	return {
		firstName: parts[0],
		lastName: parts.slice(1).join(" "),
	};
}

export function mapAdminEntryFilters(input: unknown): AdminEntryFilterData {
	const parsed = filtersResponseSchema.parse(input);
	const timekeepers = parsed.timekeepers.map((option) => {
		const name = splitName(option.label);
		return {
			id: value(option),
			...name,
			fullName: option.label,
			email: option.email ?? undefined,
		};
	});

	return {
		fieldOptions: {
			status: parsed.statuses.map((option) => ({
				value: value(option),
				label: option.label,
			})),
			timekeeper: parsed.timekeepers.map((option) => ({
				value: value(option),
				label: option.label,
				sublabel: option.email ?? undefined,
			})),
			application: parsed.applications.map((option) => ({
				value: option.label,
				label: option.label,
			})),
			matter: parsed.matters.map((option) => ({
				value: value(option),
				label: option.label,
				sublabel: option.description ?? option.client ?? undefined,
			})),
			client: parsed.clients.map((option) => ({
				value: value(option),
				label: option.label,
			})),
			billableCategory: parsed.matterBillableCategories.map((option) => ({
				value: value(option),
				label: option.label,
			})),
			task: parsed.taskCodes.map((option) => ({
				value: option.code ?? value(option),
				label: option.label,
			})),
			activity: parsed.activityCodes.map((option) => ({
				value: option.code ?? value(option),
				label: option.label,
			})),
		},
		timekeepers,
		applications: new Map(
			parsed.applications.map((option) => [value(option), option.label]),
		),
		matters: parsed.matters.map((option) => ({
			matter_id: value(option),
			matter: option.label,
			client: option.client ?? "",
			description: option.description ?? option.label,
			task_group: "",
			activity_group: "",
			billable_category: option.billableCategory ?? "unknown",
			billing_category: option.billingCategory ?? "unknown",
		})),
	};
}

export function mapAdminEntryStats(input: unknown): EntryGroupStats {
	return entryGroupStatsSchema.parse(input);
}

export type AdminEntryQuery = {
	filters: EntryFilterState;
	/** Missing means all firm timekeepers; an empty array intentionally matches none. */
	timekeeperIds?: string[];
	from: Date;
	to: Date;
	search: string;
	sorting: SortingState;
};

type QueryWhere = Record<string, unknown>;

export function buildAdminEntryWhere(query: AdminEntryQuery): QueryWhere {
	const where: QueryWhere = {
		status: { $in: query.filters.status },
		lastEntryAt: {
			$gte: new Date(
				query.from.getFullYear(),
				query.from.getMonth(),
				query.from.getDate(),
			).toISOString(),
			$lt: addDays(
				new Date(
					query.to.getFullYear(),
					query.to.getMonth(),
					query.to.getDate(),
				),
				1,
			).toISOString(),
		},
	};

	if (query.timekeeperIds !== undefined) {
		where["timekeeper.profId"] = { $in: query.timekeeperIds };
	}
	const mappings = [
		["application", "application.name"],
		["matter", "matterId"],
		["client", "matter.client"],
		["billableCategory", "matter.billableCategory"],
		["task", "taskCode.code"],
		["activity", "activityCode.code"],
	] as const;
	for (const [filterField, whereField] of mappings) {
		const selected = query.filters[filterField];
		if (selected !== undefined) {
			where[whereField] = { $in: selected };
		}
	}

	const search = query.search.trim();
	if (search) where.q = { $contains: search };
	return where;
}

const SERVER_SORT_FIELDS: Record<string, string> = {
	date: "lastEntryAt",
	status: "status",
	units: "durationSeconds",
	timekeeper: "timekeeperName",
};

export function buildAdminEntrySort(
	sorting: SortingState,
): Record<string, 1 | -1> {
	const active = sorting[0];
	const field = active ? SERVER_SORT_FIELDS[active.id] : undefined;
	if (!active || !field) return { lastEntryAt: -1 };
	return { [field]: active.desc ? -1 : 1 };
}

export function matterMap(matters: Matter[]): Map<string, Matter> {
	return new Map(matters.map((matter) => [matter.matter_id, matter]));
}
